import { DataTable, type FiltersConfig, type FiltersState } from '@/components/DataTable';
import FullPageError from '@/components/FullPageError';
import PortalTooltip from '@/components/PortalTooltip';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { propertyBlacklist } from '@/core/config';
import { execute } from '@/graphql/execute';
import { ExportType, LayersQuery } from '@/graphql/graphql';
import { useDebounce } from '@/hooks/useDebounce';
import { useOrganizations } from '@/hooks/useOrganizations';
import { deleteFeaturesMutation, downloadFeaturesMutation, featuresPaginatedQuery } from '@/queries/feature.query';
import { layerPropertyValuesQuery } from '@/queries/layer.query';
import { Download, InfoCircleSolid } from '@mynaui/icons-react';
import { ErrorBoundary } from '@sentry/react';
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SortingState } from '@tanstack/react-table';
import { ChevronDown, Loader2, Pencil, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { BulkEditDrawer } from './BulkEditDrawer';

interface iTableProps {
  projectId: string;
  layerId: string;
  selection?: string[];
  onSelect?: (selection: string[]) => void;
  selectionKey?: string;
  layers: LayersQuery['layers'];
}
export const Table = ({ projectId, layerId, selection, onSelect, selectionKey, layers }: iTableProps) => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [filters, setFilters] = useState<FiltersState>({});
  const [searchInput, setSearchInput] = useState('');
  const searchQuery = useDebounce(() => searchInput.trim(), [searchInput], 300);
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [contextMenu, setContextMenu] = useState<{ feature: any; x: number; y: number } | null>(null);
  const [contextEditFeatureId, setContextEditFeatureId] = useState<string | null>(null);
  const [deleteConfirmFeature, setDeleteConfirmFeature] = useState<{ id: string } | null>(null);
  const { hasRole } = useOrganizations();
  const queryClient = useQueryClient();

  const hasSelection = selection && selection.length > 0;

  const downloadMutation = useMutation({
    mutationFn: async ({ exportType }: { exportType: ExportType }) => {
      return execute(downloadFeaturesMutation, {
        ids: hasSelection ? selection : undefined,
        layerId: hasSelection ? undefined : layerId,
        exportType,
      });
    },
    onMutate: () => {
      return { toastId: toast.loading('Downloading features...') };
    },
    onSuccess: (data, _, context) => {
      const url = (data as any)?.downloadFeatures;
      if (url) {
        window.open(url, '_blank');
        toast.success('Download ready', { id: context?.toastId });
      }
    },
    onError: (error: Error, _, context) => {
      toast.error(error.message || 'Failed to download features', { id: context?.toastId });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!hasSelection) return;
      return execute(deleteFeaturesMutation, { ids: selection });
    },
    onMutate: () => {
      return { toastId: toast.loading('Deleting features...') };
    },
    onSuccess: (_data, _vars, context) => {
      const count = selection?.length || 0;
      toast.success(`${count} feature${count === 1 ? '' : 's'} deleted`, { id: context?.toastId });
      onSelect?.([]);
      queryClient.invalidateQueries({ queryKey: ['features-paginated', projectId, layerId] });
      queryClient.invalidateQueries({ queryKey: ['features', layerId] });
      queryClient.invalidateQueries({ queryKey: ['layer-property-values', layerId] });
    },
    onError: (error: Error, _vars, context) => {
      toast.error(error.message || 'Failed to delete features', { id: context?.toastId });
    },
  });

  const contextDeleteMutation = useMutation({
    mutationFn: async (featureId: string) => {
      return execute(deleteFeaturesMutation, { ids: [featureId] });
    },
    onMutate: () => {
      return { toastId: toast.loading('Deleting feature...') };
    },
    onSuccess: (_data, featureId, context) => {
      toast.success('Feature deleted', { id: context?.toastId });
      if (selection?.includes(featureId)) {
        onSelect?.(selection.filter((id) => id !== featureId));
      }
      queryClient.invalidateQueries({ queryKey: ['features-paginated', projectId, layerId] });
      queryClient.invalidateQueries({ queryKey: ['features', layerId] });
      queryClient.invalidateQueries({ queryKey: ['layer-property-values', layerId] });
    },
    onError: (error: Error, _vars, context) => {
      toast.error(error.message || 'Failed to delete feature', { id: context?.toastId });
    },
  });

  useEffect(() => {
    setFilters({});
    setSearchInput('');
  }, [layerId]);

  const orderBy = useMemo(() => {
    if (sorting.length === 0) return undefined;
    const sort = sorting[0];
    const direction: 'ASC' | 'DESC' = sort.desc ? 'DESC' : 'ASC';

    return { field: sort.id, direction };
  }, [sorting]);

  const { data: propertyValuesData } = useQuery({
    queryKey: ['layer-property-values', layerId],
    queryFn: () => execute(layerPropertyValuesQuery, { layerId }),
    enabled: !!layerId,
  });

  const backendFilters = useMemo(() => {
    const result: Array<{ key: string; values: string[] }> = [];
    for (const [columnId, filterValue] of Object.entries(filters)) {
      if (filterValue.type === 'options' && filterValue.values.length > 0) {
        const key = columnId.replace('properties.', '');
        result.push({ key, values: filterValue.values });
      }
    }
    return result.length > 0 ? result : undefined;
  }, [filters]);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isFetching,
    isPlaceholderData,
    isError,
    error,
  } = useInfiniteQuery({
    queryKey: ['features-paginated', projectId, layerId, orderBy, backendFilters, searchQuery],
    queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
      execute(featuresPaginatedQuery, {
        projectId,
        layerId: layerId || undefined,
        cursor: pageParam || undefined,
        limit: 100,
        orderBy: orderBy || undefined,
        filters: backendFilters,
        query: searchQuery || undefined,
      }),
    getNextPageParam: (lastPage) => {
      const cursor = (lastPage as any)?.featuresPaginated?.cursor;
      return cursor || undefined;
    },
    initialPageParam: undefined as string | undefined,
    enabled: !!projectId && !!layerId && !!projectId && !!layerId,
    placeholderData: keepPreviousData,
  });

  const handleSortingChange = (updater: SortingState | ((old: SortingState) => SortingState)) => {
    const newSorting = typeof updater === 'function' ? updater(sorting) : updater;
    setSorting(newSorting);
  };

  const features = data?.pages.flatMap((page) => (page as any)?.featuresPaginated?.features || []) || [];

  if (isError) return <FullPageError title='Error loading attributes' error={error as Error} />;

  const columns = Object.keys(features[0]?.properties || {})
    .filter((key) => !propertyBlacklist.includes(key))
    .map((key) => ({
      id: `properties.${key}`,
      header: key,
      accessorKey: `properties.${key}`,
    }));

  const filtersConfig = useMemo<FiltersConfig>(() => {
    const config: FiltersConfig = {};
    const propertyValues = (propertyValuesData as any)?.layerPropertyValues || [];

    columns.forEach((col) => {
      const propertyKey = col.id.replace('properties.', '');
      const pv = propertyValues.find((p: any) => p.property === propertyKey);

      if (pv && pv.values.length > 0) {
        config[col.id] = {
          type: 'options',
          options: pv.values.map((v: string) => ({ label: v, value: v })),
        };
      } else {
        config[col.id] = { type: 'text' };
      }
    });
    return config;
  }, [columns, propertyValuesData]);

  return (
    <>
      <div className='p-4 pb-0 h-full flex flex-col'>
        <div className='flex-1 flex flex-col select-none'>
          <ErrorBoundary
            fallback={<div className='flex flex-col items-center justify-center px-4'>Failed to load layers</div>}>
            <div className='flex items-center mb-4'>
              <Input
                className='w-64 mr-auto'
                placeholder='Search features'
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
              <div className='flex items-center gap-2'>
                {hasSelection && hasRole('EDITOR') && (
                  <Button variant='outline' className='h-9' onClick={() => setBulkEditOpen(true)}>
                    <Pencil className='size-4' />
                    Edit
                  </Button>
                )}
                {hasSelection && hasRole('ADMIN') && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant='destructive' className='h-9' disabled={deleteMutation.isPending}>
                        {deleteMutation.isPending ? (
                          <Loader2 className='size-4 animate-spin' />
                        ) : (
                          <Trash2 className='size-4' />
                        )}
                        Delete
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This will permanently delete {selection.length} feature{selection.length === 1 ? '' : 's'}.
                          This action cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction variant='destructive' onClick={() => deleteMutation.mutate()}>
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
                <div className='flex'>
                  <Button
                    variant='outline'
                    className='rounded-r-none h-9'
                    disabled={downloadMutation.isPending}
                    onClick={() => downloadMutation.mutate({ exportType: 'CSV' as ExportType })}>
                    {downloadMutation.isPending ? (
                      <Loader2 className='size-4 animate-spin' />
                    ) : (
                      <Download className='size-4' />
                    )}
                    {hasSelection ? 'Download features' : 'Download all features'}
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant='outline'
                        className='rounded-l-none border-l-0 px-2.5!'
                        disabled={downloadMutation.isPending}>
                        <ChevronDown className='size-4' />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align='end'>
                      <DropdownMenuItem onClick={() => downloadMutation.mutate({ exportType: 'CSV' as ExportType })}>
                        Download as CSV
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => downloadMutation.mutate({ exportType: 'GEOJSON' as ExportType })}>
                        Download as GeoJSON
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            </div>
            <DataTable
              total={(data?.pages[0] as any)?.featuresPaginated?.total || 0}
              columns={[
                {
                  id: 'serial',
                  header: (
                    <span className='flex items-center gap-1'>
                      <InfoCircleSolid
                        data-tooltip-id='attribute-table-serial-tooltip'
                        className='size-3.5 opacity-60'
                      />
                      SGID
                    </span>
                  ) as any,
                  enableSorting: false,
                  accessorKey: 'serial',
                },
                ...columns,
              ]}
              data={features}
              loading={isLoading}
              hasMore={hasNextPage}
              isLoadingMore={isFetchingNextPage}
              onLoadMore={() => fetchNextPage().then(() => {})}
              sorting={sorting}
              onSortingChange={handleSortingChange}
              selection={selection}
              onSelect={onSelect}
              selectionKey={selectionKey}
              filtersConfig={filtersConfig}
              filters={filters}
              onFilterChange={setFilters}
              highlightText={searchQuery || undefined}
              onRowContextMenu={(event, feature) => {
                if (hasRole('EDITOR')) {
                  setContextMenu({ feature, x: event.clientX, y: event.clientY });
                }
              }}
            />
          </ErrorBoundary>
        </div>
        <div className='flex items-center justify-between gap-4 py-4'>
          <div className='flex items-center gap-2'>
            {isPlaceholderData && isFetching && (
              <>
                <Loader2 className='size-4 animate-spin text-muted-foreground' />
                <span className='text-sm text-muted-foreground'>Loading...</span>
              </>
            )}
          </div>
          <div className='flex items-center gap-4'>
            {selection && selection.length > 0 && (
              <span className='text-sm font-[450]'>Selected features: {selection.length}</span>
            )}
            <span className='text-sm text-muted-foreground'>
              Total features: {(data?.pages[0] as any)?.featuresPaginated?.total || 0}
            </span>
          </div>
        </div>
      </div>
      <PortalTooltip id='attribute-table-serial-tooltip'>
        The internal ID of the feature, generated by SkyGIS. This is not the same as the feature ID.
      </PortalTooltip>
      <BulkEditDrawer
        open={bulkEditOpen}
        onClose={() => setBulkEditOpen(false)}
        featureIds={selection || []}
        layerId={layerId}
        projectId={projectId}
        layers={layers}
      />
      <BulkEditDrawer
        open={!!contextEditFeatureId}
        onClose={() => setContextEditFeatureId(null)}
        featureIds={contextEditFeatureId ? [contextEditFeatureId] : []}
        layerId={layerId}
        projectId={projectId}
        layers={layers}
      />
      {contextMenu && (
        <DropdownMenu
          open
          onOpenChange={(open) => {
            if (!open) setContextMenu(null);
          }}>
          <DropdownMenuContent
            className='whitespace-nowrap'
            style={{
              position: 'fixed',
              left: Math.min(contextMenu.x, window.innerWidth - 160),
              top: Math.min(contextMenu.y, window.innerHeight - 80),
              zIndex: 1000,
            }}>
            <DropdownMenuGroup>
              {hasRole('EDITOR') && (
                <DropdownMenuItem
                  onClick={() => {
                    setContextEditFeatureId(contextMenu.feature.id);
                    setContextMenu(null);
                  }}>
                  <Pencil className='size-4' />
                  Edit Feature
                </DropdownMenuItem>
              )}
              {hasRole('ADMIN') && (
                <DropdownMenuItem
                  variant='destructive'
                  onClick={() => {
                    setDeleteConfirmFeature({ id: contextMenu.feature.id });
                    setContextMenu(null);
                  }}>
                  <Trash2 className='size-4' />
                  Delete Feature
                </DropdownMenuItem>
              )}
            </DropdownMenuGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      <AlertDialog
        open={!!deleteConfirmFeature}
        onOpenChange={(open) => {
          if (!open) setDeleteConfirmFeature(null);
        }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this feature. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant='destructive'
              onClick={() => {
                if (deleteConfirmFeature) {
                  contextDeleteMutation.mutate(deleteConfirmFeature.id);
                }
                setDeleteConfirmFeature(null);
              }}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
